import { useEffect, useState, useCallback } from "react";
import { Trash2, KeyRound, UserX, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import ListToolbar from "@/components/ListToolbar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const STAGE = {
  registered: { label: "Registered only", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  password_set: { label: "Password set, profile pending", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  profile_completed: { label: "Filled form · no payment", cls: "bg-rose-100 text-rose-700 border-rose-200" },
};

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function IncompleteSignups() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    p.set("incomplete", "true");
    if (tab !== "all") p.set("role", tab);
    if (search) p.set("q", search);
    p.set("sort", sort);
    const { data } = await api.get(`/admin/sellers?${p.toString()}`);
    setRows(data);
  }, [tab, search, sort]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this incomplete signup and all their data? This cannot be undone.")) return;
    try { await api.delete(`/admin/sellers/${id}`); toast.success("Signup removed"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const resetPassword = async (row) => {
    if (!window.confirm(`Reset password for ${row.mobile}? It becomes their mobile number and they must change it at next login.`)) return;
    try {
      const { data } = await api.post(`/admin/sellers/${row.id}/reset-password`);
      toast.success(`Password reset. Temporary password: ${data.temporary_password}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="incomplete-signups-page">
      <div>
        <h1 className="font-heading text-2xl font-extrabold flex items-center gap-2">
          <UserX className="h-6 w-6 text-rose-500" /> Incomplete Signups
        </h1>
        <p className="text-muted-foreground text-sm font-medium">Sellers who started but never completed a paid subscription. These are not counted as prospects.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="incomplete-tab-all">All</TabsTrigger>
          <TabsTrigger value="manufacturer" data-testid="incomplete-tab-manufacturer">Manufacturers</TabsTrigger>
          <TabsTrigger value="wholesaler" data-testid="incomplete-tab-wholesaler">Wholesalers</TabsTrigger>
          <TabsTrigger value="retailer" data-testid="incomplete-tab-retailer">Retailers</TabsTrigger>
        </TabsList>
      </Tabs>

      <ListToolbar
        placeholder="Search company, mobile, contact…"
        search={search} setSearch={setSearch}
        filters={[]}
        onFilter={() => {}}
        sort={sort} setSort={setSort}
        sorts={[{ value: "newest", label: "Newest" }, { value: "oldest", label: "Oldest" }, { value: "company", label: "Company Name" }]}
        onReset={() => { setSearch(""); setSort("newest"); }}
      />

      <div className="bg-card border rounded-xl overflow-x-auto ct-scroll">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Company / Contact</TableHead><TableHead>Type</TableHead><TableHead>Mobile</TableHead>
            <TableHead>Drop-off Stage</TableHead><TableHead>Registered</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => {
              const st = STAGE[r.stage] || STAGE.registered;
              const name = r.company_name && r.company_name !== "—" ? r.company_name : (r.contact_person || "Unnamed lead");
              return (
                <TableRow key={r.id} data-testid={`incomplete-row-${r.id}`}>
                  <TableCell className="font-semibold">{name}<div className="text-xs text-muted-foreground">{r.trade_name || r.contact_person || ""}</div></TableCell>
                  <TableCell className="capitalize">{r.role}</TableCell>
                  <TableCell className="font-mono text-xs">{r.mobile}</TableCell>
                  <TableCell><Badge className={`border ${st.cls}`} data-testid={`incomplete-stage-${r.id}`}>{st.label}</Badge>
                  </TableCell>
                  <TableCell className="text-xs">{fmtDate(r.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" data-testid={`whatsapp-lead-${r.id}`} title="WhatsApp this lead"
                        onClick={() => window.open(`https://wa.me/91${r.mobile}?text=${encodeURIComponent("Hi! This is Cleartile.in. You started registering as a seller but haven't activated a plan yet. Complete your subscription to list your clearance stock and start getting buyer enquiries.")}`, "_blank")}>
                        <MessageCircle className="h-4 w-4 text-[#25D366]" />
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`reset-incomplete-${r.id}`} title="Reset password" onClick={() => resetPassword(r)}><KeyRound className="h-4 w-4 text-amber-600" /></Button>
                      <Button size="sm" variant="outline" data-testid={`delete-incomplete-${r.id}`} onClick={() => remove(r.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground font-semibold">No incomplete signups.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
